import {
  getSpinMotionDurationMs,
  getSpinSpeedRps,
  sanitizeSettings,
} from "../config/wheelConfig";

const GRAPH_WIDTH = 420;
const GRAPH_HEIGHT = 260;
const PADDING = {
  top: 18,
  right: 18,
  bottom: 44,
  left: 62,
};

const formatSeconds = (milliseconds) => `${(milliseconds / 1000).toFixed(1)}s`;
const formatSpeed = (speedRps) => `${speedRps.toFixed(2)} r/s`;

const buildProfile = ({
  label,
  color,
  spins,
  durationMs,
  decelerationCoefficient,
  winnerDelayMs,
  spinSoundMuteDurationMs,
}) => {
  const motionDurationMs = getSpinMotionDurationMs({ durationMs });
  const winnerFoundMs = motionDurationMs + winnerDelayMs;
  const soundStartsMs = spinSoundMuteDurationMs < motionDurationMs
    ? spinSoundMuteDurationMs
    : null;
  const points = [];
  const sampleCount = 60;

  for (let index = 0; index <= sampleCount; index += 1) {
    const elapsedMs = (winnerFoundMs * index) / sampleCount;
    points.push({
      elapsedMs,
      speedRps: getSpinSpeedRps({
        spins,
        durationMs,
        decelerationCoefficient,
        elapsedMs,
      }),
    });
  }

  return {
    label,
    color,
    spins,
    durationMs,
    motionDurationMs,
    winnerFoundMs,
    soundStartsMs,
    soundStartSpeedRps:
      soundStartsMs === null
        ? 0
        : getSpinSpeedRps({
            spins,
            durationMs,
            decelerationCoefficient,
            elapsedMs: soundStartsMs,
          }),
    peakSpeedRps: points.reduce((maxSpeed, point) => Math.max(maxSpeed, point.speedRps), 0),
    points,
  };
};

const getPath = (points, toX, toY) => {
  return points
    .map((point, index) => `${index === 0 ? "M" : "L"} ${toX(point.elapsedMs)} ${toY(point.speedRps)}`)
    .join(" ");
};

export default function WheelSettingsGraph({ settings }) {
  const safeSettings = sanitizeSettings(settings);
  const slowProfile = buildProfile({
    label: "Slowest allowed",
    color: "#78c6ff",
    spins: safeSettings.minSpins,
    durationMs: safeSettings.maxDurationMs,
    decelerationCoefficient: safeSettings.decelerationCoefficient,
    winnerDelayMs: safeSettings.winnerDelayMs,
    spinSoundMuteDurationMs: safeSettings.spinSoundMuteDurationMs,
  });
  const fastProfile = buildProfile({
    label: "Fastest allowed",
    color: "#5af2bf",
    spins: safeSettings.maxSpins,
    durationMs: safeSettings.minDurationMs,
    decelerationCoefficient: safeSettings.decelerationCoefficient,
    winnerDelayMs: safeSettings.winnerDelayMs,
    spinSoundMuteDurationMs: safeSettings.spinSoundMuteDurationMs,
  });

  const profiles = [slowProfile, fastProfile];
  const plotWidth = GRAPH_WIDTH - PADDING.left - PADDING.right;
  const plotHeight = GRAPH_HEIGHT - PADDING.top - PADDING.bottom;
  const maxTimeMs = Math.max(...profiles.map((profile) => profile.winnerFoundMs), 1);
  const maxSpeedRps = Math.max(...profiles.map((profile) => profile.peakSpeedRps), 0.1) * 1.1;
  const soundStartMarkerMs = Math.min(safeSettings.spinSoundMuteDurationMs, maxTimeMs);
  const mutedRegionWidth = Math.max(toSafeWidth(soundStartMarkerMs, maxTimeMs, plotWidth), 0);

  const toX = (milliseconds) => PADDING.left + (milliseconds / maxTimeMs) * plotWidth;
  const toY = (speedRps) => PADDING.top + plotHeight - (speedRps / maxSpeedRps) * plotHeight;

  const xTicks = Array.from({ length: 5 }, (_, index) => (maxTimeMs * index) / 4);
  const yTicks = Array.from({ length: 5 }, (_, index) => (maxSpeedRps * (4 - index)) / 4);

  return (
    <div style={styles.card}>
      <div style={styles.headerRow}>
        <div>
          <div style={styles.title}>Spin Preview</div>
          <div style={styles.subtitle}>
            Speed over time for the slowest and fastest spins your current settings allow.
            Deceleration coefficient: {safeSettings.decelerationCoefficient.toFixed(1)}.
          </div>
        </div>
      </div>

      <svg
        viewBox={`0 0 ${GRAPH_WIDTH} ${GRAPH_HEIGHT}`}
        role="img"
        aria-label="Wheel speed over time graph"
        style={styles.graph}
      >
        <rect x="0" y="0" width={GRAPH_WIDTH} height={GRAPH_HEIGHT} rx="18" fill="rgba(8, 20, 40, 0.88)" />
        <rect
          x={PADDING.left}
          y={PADDING.top}
          width={mutedRegionWidth}
          height={plotHeight}
          fill="rgba(255, 193, 93, 0.12)"
        />

        {yTicks.map((tick) => (
          <g key={`y-${tick}`}>
            <line
              x1={PADDING.left}
              y1={toY(tick)}
              x2={GRAPH_WIDTH - PADDING.right}
              y2={toY(tick)}
              stroke="rgba(130, 180, 220, 0.14)"
              strokeWidth="1"
            />
            <text
              x={PADDING.left - 10}
              y={toY(tick) + 4}
              textAnchor="end"
              fill="rgba(195, 220, 255, 0.7)"
              fontSize="11"
            >
              {formatSpeed(tick)}
            </text>
          </g>
        ))}

        {xTicks.map((tick) => (
          <g key={`x-${tick}`}>
            <line
              x1={toX(tick)}
              y1={PADDING.top}
              x2={toX(tick)}
              y2={GRAPH_HEIGHT - PADDING.bottom}
              stroke="rgba(130, 180, 220, 0.12)"
              strokeWidth="1"
            />
            <text
              x={toX(tick)}
              y={GRAPH_HEIGHT - PADDING.bottom + 20}
              textAnchor="middle"
              fill="rgba(195, 220, 255, 0.7)"
              fontSize="11"
            >
              {formatSeconds(tick)}
            </text>
          </g>
        ))}

        <line
          x1={PADDING.left}
          y1={GRAPH_HEIGHT - PADDING.bottom}
          x2={GRAPH_WIDTH - PADDING.right}
          y2={GRAPH_HEIGHT - PADDING.bottom}
          stroke="rgba(220, 240, 255, 0.38)"
          strokeWidth="1.5"
        />
        <line
          x1={PADDING.left}
          y1={PADDING.top}
          x2={PADDING.left}
          y2={GRAPH_HEIGHT - PADDING.bottom}
          stroke="rgba(220, 240, 255, 0.38)"
          strokeWidth="1.5"
        />
        <line
          x1={toX(soundStartMarkerMs)}
          y1={PADDING.top}
          x2={toX(soundStartMarkerMs)}
          y2={GRAPH_HEIGHT - PADDING.bottom}
          stroke="rgba(255, 193, 93, 0.85)"
          strokeDasharray="4 4"
          strokeWidth="1.5"
        />

        {profiles.map((profile) => (
          <g key={profile.label}>
            <line
              x1={toX(profile.motionDurationMs)}
              y1={PADDING.top}
              x2={toX(profile.motionDurationMs)}
              y2={GRAPH_HEIGHT - PADDING.bottom}
              stroke={profile.color}
              strokeDasharray="5 5"
              strokeOpacity="0.45"
            />
            <path
              d={getPath(profile.points, toX, toY)}
              fill="none"
              stroke={profile.color}
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle
              cx={toX(profile.winnerFoundMs)}
              cy={toY(0)}
              r="4"
              fill={profile.color}
            />
            {profile.soundStartsMs !== null && (
              <circle
                cx={toX(profile.soundStartsMs)}
                cy={toY(profile.soundStartSpeedRps)}
                r="4"
                fill="#ffc15d"
                stroke="rgba(8, 20, 40, 0.88)"
                strokeWidth="1.5"
              />
            )}
          </g>
        ))}

        <text
          x={PADDING.left + plotWidth / 2}
          y={GRAPH_HEIGHT - 10}
          textAnchor="middle"
          fill="rgba(225, 240, 255, 0.82)"
          fontSize="12"
        >
          Time elapsed from pressing Spin to winner found
        </text>
        <text
          x="16"
          y={PADDING.top + plotHeight / 2}
          textAnchor="middle"
          fill="rgba(225, 240, 255, 0.82)"
          fontSize="12"
          transform={`rotate(-90 16 ${PADDING.top + plotHeight / 2})`}
        >
          Wheel speed (rotations/sec)
        </text>
      </svg>

      <div style={styles.legend}>
        {profiles.map((profile) => (
          <div key={profile.label} style={styles.legendItem}>
            <span style={{ ...styles.legendSwatch, background: profile.color }} />
            <span style={styles.legendText}>
              {profile.label}: {profile.spins} spins, winner at {formatSeconds(profile.winnerFoundMs)}
            </span>
          </div>
        ))}
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendSwatch, background: "#ffc15d" }} />
          <span style={styles.legendText}>
            Sound starts at {formatSeconds(safeSettings.spinSoundMuteDurationMs)} unless the wheel has already stopped.
          </span>
        </div>
      </div>

      <div style={styles.note}>
        The amber area is the muted part of the spin. Dashed markers show when the wheel stops moving, and the flat tail is the winner delay before the result is revealed.
      </div>
    </div>
  );
}

function toSafeWidth(milliseconds, maxTimeMs, plotWidth) {
  if (maxTimeMs <= 0) return 0;
  return (milliseconds / maxTimeMs) * plotWidth;
}

const styles = {
  card: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    minWidth: 280,
    flex: "1 1 340px",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
  },
  title: {
    color: "#fff",
    fontSize: 16,
    fontWeight: 700,
  },
  subtitle: {
    marginTop: 4,
    color: "rgba(210, 225, 245, 0.74)",
    fontSize: 12,
    lineHeight: 1.45,
  },
  graph: {
    width: "100%",
    height: "auto",
    display: "block",
  },
  legend: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    color: "#d8eaff",
    fontSize: 12,
  },
  legendSwatch: {
    width: 12,
    height: 12,
    borderRadius: 999,
    boxShadow: "0 0 10px rgba(120, 210, 255, 0.35)",
    flexShrink: 0,
  },
  legendText: {
    color: "rgba(220, 235, 255, 0.82)",
  },
  note: {
    color: "rgba(170, 195, 220, 0.72)",
    fontSize: 11,
    lineHeight: 1.45,
  },
};